import * as React from "react";
import { SkillForm } from "../components/SkillForm";
import { useNavigate } from "react-router-dom";
import Footer from "../components/Footer";

import { useTranslation } from 'react-i18next';
import SEOHead from "../components/SEOHead";
const STEPS = [
  { id: "basic", title: "Basic Information", description: "Name, description and skill category" },
  { id: "config", title: "Configuration", description: "Skill parameters and flows" },
  { id: "preview", title: "Preview", description: "Review before submitting" },
];

export default function CreateSkillPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = React.useState(0);
  const [submitted, setSubmitted] = React.useState(false);
  const [formData, setFormData] = React.useState({
    name: "", description: "", category: "", price: "", steps: "", tags: "", icon: "", version: "1.0.0",
  });

  const updateField = (field, value) => setFormData((prev) => ({ ...prev, [field]: value }));

  const canProceed = () => {
    if (currentStep === 0) return formData.name && formData.description && formData.category;
    if (currentStep === 1) return formData.steps;
    return true;
  };

  const handleSubmit = () => {
    setSubmitted(true);
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
      <SEOHead
        title="Create a Skill | Max Employee"
        description="Submit your AI skill for review. Define name, category, flow steps, and pricing. Join the Skills Marketplace."
        url="/skills/create"
      />
        <div className="text-center max-w-md">
          <div className="text-6xl mb-4">🎉</div>
          <h2 className="text-2xl font-bold text-white mb-2">{t('create_skill_page.skill_submitted_successfully')}</h2>
          <p className="text-slate-400 mb-6">{t('create_skill_page.your_skill_has_been_sent_for_review_you_will_be')}</p>
          <button onClick={() => navigate("/skills")} className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
            View Skills
          </button>
        </div>
      <Footer />

      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 p-6">
      <div className="max-w-2xl mx-auto">
        <h1 className="text-3xl font-bold text-white mb-2">{t('create_skill_page.create_new_skill')}</h1>
        <p className="text-slate-400 mb-8">{t('create_skill_page.fill_in_the_information_to_submit_your_skill_for')}</p>

        {/* Step indicator */}
        <div className="flex gap-2 mb-8">
          {STEPS.map((step, i) => (
            <div key={step.id} className={`flex-1 h-2 rounded-full ${i <= currentStep ? "bg-blue-500" : "bg-slate-700"}`} />
          ))}
        </div>

        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
          <h2 className="text-xl font-semibold text-white mb-1">{STEPS[currentStep].title}</h2>
          <p className="text-sm text-slate-400 mb-6">{STEPS[currentStep].description}</p>

          {currentStep === 0 && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">Skill Name *</label>
                <input type="text" value={formData.name} onChange={(e) => updateField("name", e.target.value)} placeholder={t('create_skill_page.ex_contract_writing')} className="w-full px-4 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white placeholder-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">Description *</label>
                <textarea value={formData.description} onChange={(e) => updateField("description", e.target.value)} rows={3} placeholder={t('create_skill_page.describe_what_the_skill_does')} className="w-full px-4 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white placeholder-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">Category *</label>
                <select value={formData.category} onChange={(e) => updateField("category", e.target.value)} className="w-full px-4 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option value="">{t('skills.select')}</option>
                  <option value="support">{t('api_docs.automations.builder.categories.support')}</option>
                  <option value="legal">{t('create_skill_page.legal')}</option>
                  <option value="finance">{t('create_skill_page.finance')}</option>
                  <option value="sales">{t('api_docs.automations.builder.categories.sales')}</option>
                  <option value="health">{t('create_skill_page.health')}</option>
                  <option value={t('create_skill_page.marketing')}>Marketing</option>
                </select>
              </div>
            </div>
          )}

          {currentStep === 1 && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">Flow Steps *</label>
                <textarea value={formData.steps} onChange={(e) => updateField("steps", e.target.value)} rows={5} placeholder={t('create_skill_page.describe_the_skill_flow_steps')} className="w-full px-4 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white placeholder-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">Price ($)</label>
                <input type="number" value={formData.price} onChange={(e) => updateField("price", e.target.value)} placeholder="0.00" className="w-full px-4 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white placeholder-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">Tags (comma separated)</label>
                <input type="text" value={formData.tags} onChange={(e) => updateField("tags", e.target.value)} placeholder="contracts, legal, automation" className="w-full px-4 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white placeholder-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
            </div>
          )}

          {currentStep === 2 && (
            <div className="space-y-3">
              <div className="bg-slate-900 rounded-lg p-4 border border-slate-700">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div><span className="text-slate-400">{t('create_skill_page.name')}</span> <span className="text-white">{formData.name || "--"}</span></div>
                  <div><span className="text-slate-400">{t('create_skill_page.category')}</span> <span className="text-white">{formData.category || "--"}</span></div>
                  <div className="col-span-2"><span className="text-slate-400">{t('create_skill_page.description')}</span> <span className="text-white">{formData.description || "--"}</span></div>
                  <div><span className="text-slate-400">{t('create_skill_page.price')}</span> <span className="text-white">{formData.price ? `$${formData.price}` : "Free"}</span></div>
                  <div><span className="text-slate-400">{t('create_skill_page.tags')}</span> <span className="text-white">{formData.tags || "--"}</span></div>
                </div>
              </div>
            </div>
          )}

          <div className="flex gap-3 mt-6">
            {currentStep > 0 && (
              <button onClick={() => setCurrentStep((s) => s - 1)} className="px-4 py-2 rounded-lg border border-slate-600 text-slate-300 hover:bg-slate-700 text-sm">
                Back
              </button>
            )}
            {currentStep < 2 ? (
              <button onClick={() => setCurrentStep((s) => s + 1)} disabled={!canProceed()} className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 text-sm font-medium">
                Next
              </button>
            ) : (
              <button onClick={handleSubmit} className="px-4 py-2 rounded-lg bg-green-600 text-white hover:bg-green-700 text-sm font-medium">{t('create_skill_page.submit_for_review')}</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
